import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch, FormHelperText } from '@mui/material';
import { useSenders } from '../../hooks/useSenders';
import { TimeValue } from '../../utils/TimeValue';

const GeneralSettings = ({ data, onUpdate }) => {
  const [formattedTime, setFormattedTime] = useState('');
  
  // Format the time for display - time inputs only accept HH:MM format
  useEffect(() => {
    console.log('🕒 GeneralSettings received time:', data.scheduleTime);
    
    if (data.scheduleTime) {
      // Handle TimeValue objects
      let timeString = '';
      
      if (data.scheduleTime instanceof TimeValue) {
        console.log('🕒 Received TimeValue object:', data.scheduleTime);
        timeString = data.scheduleTime.value || '';
      } else if (typeof data.scheduleTime === 'object' && data.scheduleTime !== null) {
        // Handle other objects that might have a value property or toString method
        if (data.scheduleTime.value && typeof data.scheduleTime.value === 'string') {
          console.log('🕒 Extracted value from object:', data.scheduleTime.value);
          timeString = data.scheduleTime.value;
        } else if (data.scheduleTime.toString && typeof data.scheduleTime.toString === 'function') {
          console.log('🕒 Using toString() method:', data.scheduleTime.toString());
          timeString = data.scheduleTime.toString();
        }
      } else if (typeof data.scheduleTime === 'string') {
        // Plain string - use directly
        timeString = data.scheduleTime;
      }
      
      // Now that we have a string, extract HH:MM from HH:MM:SS if needed
      if (timeString && timeString.includes(':')) {
        const timeParts = timeString.split(':');
        const timeValue = timeParts.slice(0, 2).join(':');
        console.log('🕒 Formatting to HH:MM for input:', timeValue);
        setFormattedTime(timeValue);
      } else {
        console.log('🕒 No valid time string found in:', data.scheduleTime);
        setFormattedTime('');
      }
    } else {
      setFormattedTime('');
    }
  }, [data.scheduleTime]);
  
  const errors = {
    title: !data.title,
    scheduleDays: data.scheduleType === 'WEEKLY' && (!data.scheduleDays || data.scheduleDays.length === 0),
    scheduleDay: data.scheduleType === 'MONTHLY' && (!data.scheduleDay),
    emailFromId: !data.emailFromId
  };

  const { senders, isLoading } = useSenders();
  
  // Handle time change with proper formatting
  const handleTimeChange = (e) => {
    const newTime = e.target.value;
    console.log('🕒 Time changed to:', newTime);
    
    // Extract the current time string if it exists
    let currentTimeString = '';
    if (data.scheduleTime instanceof TimeValue) {
      currentTimeString = data.scheduleTime.value || '';
    } else if (typeof data.scheduleTime === 'object' && data.scheduleTime !== null && data.scheduleTime.value) {
      currentTimeString = data.scheduleTime.value;
    } else if (typeof data.scheduleTime === 'string') {
      currentTimeString = data.scheduleTime;
    }
    
    // Preserve the original format with seconds when updating
    let updatedTime = newTime;
    if (currentTimeString && currentTimeString.includes(':')) {
      const parts = currentTimeString.split(':');
      if (parts.length === 3) {
        updatedTime = `${newTime}:00`;  // Add seconds back if the original had seconds
        console.log('🕒 Adding seconds back:', updatedTime);
      }
    }
      
    console.log('🕒 Updating with value:', updatedTime);
    onUpdate({ scheduleTime: updatedTime });
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        General Settings
      </Typography>

      <TextField
        label="Automation Title"
        fullWidth
        required
        value={data.title || ''}
        onChange={(e) => onUpdate({ title: e.target.value })}
        error={errors.title}
        helperText={errors.title ? 'Title is required' : ''}
        sx={{ mb: 3 }}
      />

      <FormControl fullWidth sx={{ mb: 3 }}>
        <InputLabel>Schedule Type</InputLabel>
        <Select
          value={data.scheduleType || 'DAILY'}
          label="Schedule Type"
          onChange={(e) => onUpdate({ scheduleType: e.target.value })}
        >
          <MenuItem value="HOURLY">Hourly</MenuItem>
          <MenuItem value="DAILY">Daily</MenuItem>
          <MenuItem value="WEEKLY">Weekly</MenuItem>
          <MenuItem value="MONTHLY">Monthly</MenuItem>
        </Select>
      </FormControl>

      {data.scheduleType === 'WEEKLY' && (
        <FormControl fullWidth sx={{ mb: 3 }} error={errors.scheduleDays}>
          <InputLabel>Days of Week</InputLabel>
          <Select
            multiple
            value={data.scheduleDays || []}
            label="Days of Week"
            onChange={(e) => onUpdate({ scheduleDays: e.target.value })}
            renderValue={(selected) => selected.join(', ')}
          >
            <MenuItem value="Monday">Monday</MenuItem>
            <MenuItem value="Tuesday">Tuesday</MenuItem>
            <MenuItem value="Wednesday">Wednesday</MenuItem>
            <MenuItem value="Thursday">Thursday</MenuItem>
            <MenuItem value="Friday">Friday</MenuItem>
            <MenuItem value="Saturday">Saturday</MenuItem>
            <MenuItem value="Sunday">Sunday</MenuItem>
          </Select>
          {errors.scheduleDays && <Typography color="error">Select at least one day</Typography>}
        </FormControl>
      )}

      {data.scheduleType === 'MONTHLY' && (
        <TextField
          label="Day of Month (1-31)"
          type="number"
          fullWidth
          value={data.scheduleDay || ''}
          onChange={(e) => onUpdate({ scheduleDay: e.target.value })}
          inputProps={{ min: 1, max: 31 }}
          error={errors.scheduleDay}
          helperText={errors.scheduleDay ? 'Enter a valid date' : ''}
          sx={{ mb: 3 }}
        />
      )}

      {data.scheduleType === 'HOURLY' && (
        <TextField
          label="Send Every X Hours"
          type="number"
          fullWidth
          value={data.scheduleInterval || ''}
          onChange={(e) => onUpdate({ scheduleInterval: parseInt(e.target.value) })}
          inputProps={{ min: 1, max: 24 }}
          sx={{ mb: 3 }}
        />
      )}

      {data.scheduleType !== 'HOURLY' && (
        <TextField
          label={`Send Time (${localStorage.getItem('timezone') || Intl.DateTimeFormat().resolvedOptions().timeZone})`}
          type="time"
          fullWidth
          value={formattedTime}
          onChange={handleTimeChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
          sx={{ mb: 3 }}
          helperText="Time will be automatically converted to UTC for scheduling"
        />
      )}

      {!isLoading && senders && senders.length > 0 && (
        <FormControl fullWidth required error={errors.emailFromId} sx={{ mb: 3 }}>
          <InputLabel id="email-sender-label">Sender</InputLabel>
          <Select
            labelId="email-sender-label"
            value={data.emailFromId || ''}
            label="Sender"
            onChange={(e) => onUpdate({ emailFromId: e.target.value })}
          >
            {senders.map((sender) => (
              <MenuItem key={sender.id} value={sender.id}>
                {sender.display_name} &lt;{sender.email}&gt;
              </MenuItem>
            ))}
          </Select>
          {errors.emailFromId && <FormHelperText>Sender is required</FormHelperText>}
        </FormControl>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={data.preventDuplicates || false}
            onChange={(e) => onUpdate({ preventDuplicates: e.target.checked })}
          />
        }
        label="Don't send to the same client more than once"
        sx={{ mb: 3 }}
      />
    </Box>
  );
};

export default GeneralSettings;